import React from "react";
import type { User } from "@/lib/types/user";
import type { SelectOption } from "@/components/ui/responsive-select/types";
import {
  UserOptionPreview,
  humanizeHubRole,
} from "@/components/ui/selects/components/UserOptionPreview";

/**
 * Builds the option list the user picker hands to ResponsiveCommandMultiSelect.
 *
 * Split out of `MultiUserSelect.tsx` (module-size lane L17). Behaviour is
 * pinned by `tests/components-ui/MultiUserSelect.test.tsx`.
 */

/** Loose shape covering both the directory `User` row and the GraphQL `AppUser`
 * search result — enough to build a rich option preview from either source. */
export interface UserOptionSource {
  first_name?: string | null;
  last_name?: string | null;
  email?: string | null;
  role?: string | null;
  roles?: { name?: string | null } | null;
  is_active?: boolean | null;
}

/** Build the rich list-item node (name + email + role) for a real user row.
 * Email is shown only when a name is present, mirroring the label fallback so the
 * subheader never duplicates a label that is already the email address. */
export function buildUserOptionNode(source: UserOptionSource): React.ReactNode {
  const fullName = `${source.first_name || ""} ${source.last_name || ""}`.trim();
  const email = source.email || undefined;
  const roleName = humanizeHubRole(
    typeof source.role === "string" ? source.role : source.roles?.name ?? undefined
  );
  return (
    <UserOptionPreview
      label={fullName || email || ""}
      email={fullName ? email : undefined}
      role={roleName}
      isActive={typeof source.is_active === "boolean" ? source.is_active : undefined}
    />
  );
}

/** A user row carrying an id, from any of the three sources below. */
interface IdentifiedUser extends UserOptionSource {
  id: string;
}

/** One user option: label falls back name -> email -> id, and the description
 * is dropped when the label already IS the email. */
function toUserOption(id: string, source: UserOptionSource): SelectOption {
  const fullName = `${source.first_name || ""} ${source.last_name || ""}`.trim();
  const email = source.email || undefined;
  return {
    value: id,
    label: fullName || email || id,
    description: fullName ? email : undefined,
    searchableText: [fullName, email].filter(Boolean).join(" "),
    node: buildUserOptionNode(source),
  };
}

/** Is this entry relation DATA (a user object) rather than a bare id string? */
function isIdentifiedUser(item: unknown): item is IdentifiedUser {
  if (typeof item !== "object" || item === null || !("id" in item)) return false;
  return Boolean((item as { id: unknown }).id);
}

/**
 * Assemble the option list, in this order:
 *
 *   1. the "current user" shortcut (always first),
 *   2. "Unassigned", when enabled,
 *   3. selected users the caller passed as relation DATA — so a selection shows
 *      a real name immediately, before the search query resolves,
 *   4. selected bare UUIDs resolved against the admin directory (covers
 *      create-form defaults / prefill),
 *   5. the search results.
 *
 * Ids are de-duplicated across 3-5, first writer wins.
 */
export function buildUserOptions({
  meOptionLabel,
  meOptionDescription,
  showUnassigned,
  currentUserIds,
  normalizedUserIds,
  userDirectory,
  searchResults,
}: {
  meOptionLabel: string;
  meOptionDescription: string;
  showUnassigned: boolean;
  currentUserIds: unknown;
  normalizedUserIds: string[];
  userDirectory: User[] | undefined;
  searchResults: IdentifiedUser[] | undefined;
}): SelectOption[] {
  const options: SelectOption[] = [];
  const seen = new Set<string>();

  options.push({
    value: "me",
    label: meOptionLabel,
    description: meOptionDescription,
    searchableText: "me assign myself yourself",
  });

  if (showUnassigned) {
    options.push({
      value: "unassigned",
      label: "Unassigned",
      description: "No assignment",
      searchableText: "unassigned none",
    });
  }

  if (Array.isArray(currentUserIds)) {
    currentUserIds.forEach((item: unknown) => {
      if (!isIdentifiedUser(item)) return;
      options.push(toUserOption(item.id, item));
      seen.add(item.id);
    });
  }

  normalizedUserIds.forEach((id) => {
    if (seen.has(id)) return;
    const row = userDirectory?.find((u: User) => u.id === id);
    if (!row) return;
    options.push(toUserOption(id, row));
    seen.add(id);
  });

  if (searchResults && searchResults.length > 0) {
    searchResults.forEach((user) => {
      if (seen.has(user.id)) return;
      options.push(toUserOption(user.id, user));
      seen.add(user.id);
    });
  }

  return options;
}
